import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Eye, Activity, Zap } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Live scarcity signals — drops onto property + city + region pages.
 *
 * Reads /api/scarcity/city/{city} once on mount + every 60s. Uses
 * existing collections (analytics_events + pro_contact_unlocks) so
 * zero new write load. The "viewers" number is honestly floored
 * at the rolling weekly average so cards never show a misleading "0".
 */
const ScarcityWidget = ({ city, country, dataTestId = 'scarcity-widget' }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!city) return;
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const params = new URLSearchParams({ city, ...(country && { country }) });
        const r = await axios.get(`${API_URL}/api/scarcity/city/${encodeURIComponent(city)}?${params}`);
        if (!cancelled) setData(r.data);
      } catch { /* silent — widget hides if it can't load */ }
    };
    fetchOnce();
    const id = setInterval(fetchOnce, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [city, country]);

  if (!data) return null;

  return (
    <div
      className="flex flex-wrap gap-3 sm:gap-4 items-center text-xs sm:text-[13px] text-slate-700 bg-amber-50 border border-amber-200 rounded p-3 mb-4"
      data-testid={dataTestId}
    >
      <span className="flex items-center gap-1.5" data-testid={`${dataTestId}-viewers`}>
        <Eye className="h-3.5 w-3.5 text-amber-600" />
        <b className="text-amber-900">{data.viewers}</b>
        <span>viewing {city} now</span>
      </span>
      {data.last_unlock_human && (
        <span className="flex items-center gap-1.5" data-testid={`${dataTestId}-last-unlock`}>
          <Activity className="h-3.5 w-3.5 text-amber-600" />
          <span>Last unlock <b className="text-amber-900">{data.last_unlock_human}</b></span>
        </span>
      )}
      {data.weekly_unlocks > 0 && (
        <span className="flex items-center gap-1.5" data-testid={`${dataTestId}-weekly`}>
          <Zap className="h-3.5 w-3.5 text-amber-600" />
          <b className="text-amber-900">{data.weekly_unlocks}</b>
          <span>unlocks this week</span>
        </span>
      )}
    </div>
  );
};

export default ScarcityWidget;
